import { z } from 'zod';
import { getPhoneValidation } from '@helpers/validation_objects/index';
import { validator } from '@xeppt/xeppt-sdk';
import { TAddress } from '@xeppt/xeppt-sdk/types/user';
import { EAddressType } from '@xeppt/xeppt-sdk/types/user';

interface IPersonalValidationProps {
    currentStep: number;
    phoneVerification: boolean;
    isEmailSent: boolean;
    messages: {
        email: string;
        phone: string;
        phoneVerifyCode: string;
        first_name: string;
        last_name: string;
        birth_date: string;
        tag: string;
        occupancy: string;
        billing_address: TAddress;
        password: string;
        password_confirm: string;
    };
}

export const signupPersonalStepsValidation = ({
    currentStep,
    messages,
    phoneVerification,
    isEmailSent
}: IPersonalValidationProps) => {
    const steps = [
        z.object({}),
        z.object({
            email: validator.email(messages.email),
            ...(isEmailSent && { code: z.string().length(6) })
        }),
        // z.object({}),
        getPhoneValidation(
            { phone: messages.phone, verification: messages.phoneVerifyCode },
            phoneVerification
        ),
        z
            .object({
                password: validator.password(messages.password),
                password_confirm: validator.password(messages.password_confirm)
            })
            .refine((data) => data.password === data.password_confirm, {
                message: messages.password_confirm,
                path: ['password_confirm']
            }),
        z.object({
            profile: z.object({
                first_name: validator.firstName(messages.first_name),
                last_name: validator.lastName(messages.last_name),
                birth_date: validator.birthday(messages.birth_date),
                tag: z.string().min(1, messages.tag),
                occupancy: z.string().min(1, messages.occupancy)
            })
        }),
        z.object({
            billing_address: validator.address(messages.billing_address)
        })
    ];
    return steps[currentStep];
};

interface IBusinessValidationProps {
    addressType: EAddressType;
    currentStep: number;
    phoneVerification: boolean;
    isEmailSent: boolean;
    messages: {
        email: string;
        phone: string;
        phoneVerifyCode: string;
        first_name: string;
        last_name: string;
        birth_date: string;
        address: TAddress;
        password: string;
        password_confirm: string;
        residence: string;
        tag: string;
        occupancy: string;
        business: {
            company_name: string;
            tradingName: string;
            registration_number: string;
            category: string;
            size: string;
            website: string;
        };
    };
}

const getAddressValidation = (type: EAddressType, messages: TAddress) => {
    if (type === EAddressType.BUSINESS) {
        return z.object({
            business_address: validator.address(messages)
        });
    } else if (type === EAddressType.TRADING) {
        return z.object({
            trading_address: validator.address(messages)
        });
    } else if (type === EAddressType.BILLING) {
        return z.object({
            billing_address: validator.address(messages)
        });
    } else {
        return z.object({
            registration_address: validator.address(messages)
        });
    }
};

export const signupBusinessStepsValidation = ({
    addressType,
    currentStep,
    messages,
    phoneVerification,
    isEmailSent
}: IBusinessValidationProps) => {
    const steps = [
        z.object({}),
        z.object({
            email: validator.email(messages.email),
            ...(isEmailSent && { code: z.string().length(6) })
        }),
        getPhoneValidation(
            { phone: messages.phone, verification: messages.phoneVerifyCode },
            phoneVerification
        ),
        // z.object({}),
        z
            .object({
                password: validator.password(messages.password),
                password_confirm: validator.password(messages.password_confirm)
            })
            .refine((data) => data.password === data.password_confirm, {
                message: messages.password_confirm,
                path: ['password_confirm']
            }),
        z.object({
            profile: z.object({
                first_name: validator.firstName(messages.first_name),
                last_name: validator.lastName(messages.last_name),
                birth_date: validator.birthday(messages.birth_date),
                tag: z.string().min(1, messages.tag),
                occupancy: z.string().min(1, messages.occupancy),
                residence: z.string().min(1, messages.residence)
            })
        }),
        z.object({
            business: z.object({
                company_name: z.string().min(1, { message: messages.business.company_name }),
                tradingName: z.string().min(1, { message: messages.business.tradingName }),
                registration_number: z
                    .string()
                    .min(1, { message: messages.business.registration_number }),
                category: z.string().min(1, { message: messages.business.category }),
                size: z.string().min(1, { message: messages.business.size }),
                website: z.string().url({ message: messages.business.website })
            })
        }),
        getAddressValidation(addressType, messages.address)
    ];
    return steps[currentStep];
};
